import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react'
import axios from 'axios'
import { StudentProfile } from '../types/models'
import { useAuth } from './AuthContext'

interface AccountContextType {
  studentProfiles: StudentProfile[]
  loading: boolean
  error: string | null
  createProfile: (name: string, yearLevel: number) => Promise<StudentProfile>
  getProfiles: () => Promise<void>
  updateProfile: (profileId: string, name?: string, yearLevel?: number) => Promise<void>
  deleteProfile: (profileId: string) => Promise<void>
  getProfile: (profileId: string) => Promise<StudentProfile | null>
}

const AccountContext = createContext<AccountContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [studentProfiles, setStudentProfiles] = useState<StudentProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated } = useAuth()
  const isFetching = useRef(false)

  const getProfiles = useCallback(async () => {
    // Prevent concurrent fetches and check auth
    if (isFetching.current || !isAuthenticated) return
    
    isFetching.current = true
    setLoading(true)
    setError(null)

    try {
      const response = await axios.get(`${API_BASE_URL}/api/account/profiles`)
      // Map profileId to id for frontend compatibility
      const profiles = (response.data.studentProfiles || []).map((p: any) => ({
        ...p,
        id: p.profileId || p.id
      }))
      setStudentProfiles(profiles)
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch profiles'
      setError(errorMessage)
      console.error('Error fetching profiles:', err)
    } finally {
      setLoading(false)
      isFetching.current = false
    }
  }, [isAuthenticated])

  const createProfile = async (name: string, yearLevel: number): Promise<StudentProfile> => {
    setLoading(true)
    setError(null)

    try {
      const response = await axios.post(`${API_BASE_URL}/api/account/profiles`, {
        name,
        yearLevel
      })

      const newProfile: StudentProfile = {
        id: response.data.profileId,
        userId: '', // Will be set by backend
        name,
        yearLevel: yearLevel as 3 | 4 | 5 | 6 | 7 | 8,
        createdAt: new Date()
      }

      setStudentProfiles([...studentProfiles, newProfile])
      return newProfile
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to create profile'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const getProfile = async (profileId: string): Promise<StudentProfile | null> => {
    setLoading(true)
    setError(null)

    try {
      const response = await axios.get(`${API_BASE_URL}/api/account/profiles/${profileId}`)
      return response.data.studentProfile
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch profile'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (profileId: string, name?: string, yearLevel?: number) => {
    setLoading(true)
    setError(null)

    try {
      await axios.put(`${API_BASE_URL}/api/account/profiles/${profileId}`, {
        name,
        yearLevel
      })

      // Update local state
      setStudentProfiles(
        studentProfiles.map((profile) =>
          profile.id === profileId
            ? {
                ...profile,
                name: name || profile.name,
                yearLevel: (yearLevel || profile.yearLevel) as 3 | 4 | 5 | 6 | 7 | 8
              }
            : profile
        )
      )
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to update profile'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const deleteProfile = async (profileId: string) => {
    setLoading(true)
    setError(null)

    try {
      await axios.delete(`${API_BASE_URL}/api/account/profiles/${profileId}`)

      // Update local state
      setStudentProfiles(studentProfiles.filter((profile) => profile.id !== profileId))
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to delete profile'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const value: AccountContextType = {
    studentProfiles,
    loading,
    error,
    createProfile,
    getProfiles,
    updateProfile,
    deleteProfile,
    getProfile
  }

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
}

export const useAccount = () => {
  const context = useContext(AccountContext)
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider')
  }
  return context
}
